import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function FraudCaseManager() {
  const [fraudCases, setFraudCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [editingCase, setEditingCase] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    caseId: '',
    name: '',
    reported: '',
    violation: '',
    status: 'Under Investigation',
    severity: 'High',
    complaints: 0,
    warningLevel: 'Critical',
    description: '',
    lastKnownLocation: '',
    aliases: '',
    victimCount: '',
    estimatedLoss: ''
  });

  // Load fraud cases from API
  useEffect(() => {
    fetchFraudCases();
  }, []);

  const fetchFraudCases = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_URL}/admin/fraud-cases`, { headers });

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminAuth');
        setTimeout(() => window.location.reload(), 1000);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setFraudCases(data);
      } else {
        console.error('Failed to fetch fraud cases');
      }
    } catch (error) {
      console.error('Error fetching fraud cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingCase(null);
    setFormData({
      caseId: `FRAUD-2024-${String(fraudCases.length + 1).padStart(3, '0')}`,
      name: '',
      reported: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      violation: '',
      status: 'Under Investigation',
      severity: 'High',
      complaints: 0,
      warningLevel: 'Critical',
      description: '',
      lastKnownLocation: '',
      aliases: '',
      victimCount: '',
      estimatedLoss: ''
    });
    setShowForm(true);
  };

  const handleEdit = (fraudCase) => {
    setEditingCase(fraudCase.caseId);
    setFormData(fraudCase);
    setShowForm(true);
  };

  const handleToggleVisibility = async (fraudCase) => {
    const willHide = !fraudCase.isHidden;
    if (!window.confirm(willHide
      ? 'Hide this fraud case from the public Fraud Alert list?'
      : 'Show this fraud case on the public Fraud Alert list?'
    )) return;

    try {
      const token = localStorage.getItem('adminToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/fraud-cases/${fraudCase.caseId}/visibility`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ hidden: willHide })
      });

      if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        return;
      }
      const result = await response.json();
      if (response.ok && result.success) {
        fetchFraudCases();
      } else {
        alert(result.message || 'Failed to update visibility');
      }
    } catch (error) {
      alert('Error updating visibility');
      console.error(error);
    }
  };

  const handleApprove = async (fraudCase) => {
    if (!window.confirm(`Approve this fraud case? It will become publicly visible and an email will be sent to ${fraudCase.email || 'the reporter'}.`)) {
      return;
    }
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch(`${API_URL}/fraud-cases/${fraudCase.caseId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ ...fraudCase, status: 'Under Investigation' }),
      });

      if (response.status === 401 || response.status === 403) {
        alert('Session expired. Please login again.');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        localStorage.removeItem('adminAuth');
        setTimeout(() => window.location.reload(), 1000);
        return;
      }

      if (response.ok) {
        fetchFraudCases();
      } else {
        alert('Failed to approve fraud case');
      }
    } catch (error) {
      console.error('Error approving fraud case:', error);
      alert('Error approving fraud case');
    }
  };

  const handleDelete = async (caseId) => {
    if (window.confirm('Are you sure you want to delete this fraud case?')) {
      try {
        const token = localStorage.getItem('adminToken');
        const headers = {
          'Content-Type': 'application/json'
        };

        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(`${API_URL}/fraud-cases/${caseId}`, {
          method: 'DELETE',
          headers
        });

        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          alert('Session expired. Please login again.');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminAuth');
          setTimeout(() => window.location.reload(), 1000);
          return;
        }

        if (response.ok) {
          fetchFraudCases(); // Reload fraud cases
        } else {
          console.error('Failed to delete fraud case');
        }
      } catch (error) {
        console.error('Error deleting fraud case:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('adminToken');
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      if (editingCase) {
        // Update existing case
        const response = await fetch(`${API_URL}/fraud-cases/${editingCase}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(formData),
        });

        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          alert('Session expired. Please login again.');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminAuth');
          setTimeout(() => window.location.reload(), 1000);
          return;
        }

        if (response.ok) {
          fetchFraudCases(); // Reload fraud cases
          setShowForm(false);
          setEditingCase(null);
        } else {
          console.error('Failed to update fraud case');
        }
      } else {
        // Add new case
        const response = await fetch(`${API_URL}/fraud-cases`, {
          method: 'POST',
          headers,
          body: JSON.stringify(formData),
        });

        // Handle authentication errors
        if (response.status === 401 || response.status === 403) {
          alert('Session expired. Please login again.');
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          localStorage.removeItem('adminAuth');
          setTimeout(() => window.location.reload(), 1000);
          return;
        }

        if (response.ok) {
          fetchFraudCases(); // Reload fraud cases
          setShowForm(false);
        } else {
          console.error('Failed to add fraud case');
        }
      }
    } catch (error) {
      console.error('Error submitting fraud case:', error);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingCase(null);
  };

  // Filter fraud cases based on search query
  const filteredFraudCases = fraudCases.filter(fraudCase => {
    if (!searchQuery.trim()) {
      return true;
    }
    const query = searchQuery.toLowerCase();
    return (
      fraudCase.caseId.toLowerCase().includes(query) ||
      fraudCase.name.toLowerCase().includes(query) ||
      fraudCase.violation.toLowerCase().includes(query) ||
      fraudCase.status.toLowerCase().includes(query) ||
      fraudCase.severity.toLowerCase().includes(query) ||
      (fraudCase.description && fraudCase.description.toLowerCase().includes(query)) ||
      (fraudCase.lastKnownLocation && fraudCase.lastKnownLocation.toLowerCase().includes(query))
    );
  });

  return (
    <div className="tab-content">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h2 style={{ margin: 0 }}>Fraud Cases Management</h2>
        <button
          onClick={handleAdd}
          className="submit-btn"
          style={{ backgroundColor: '#e74c3c' }}
        >
          + Add New Fraud Case
        </button>
      </div>

      {/* Search Bar */}
      <div style={{ marginBottom: '20px' }}>
        <input
          type="text"
          placeholder="Search by case ID, entity name, violation, status, severity, or location..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: '100%',
            padding: '12px 15px',
            fontSize: '14px',
            border: '2px solid #ddd',
            borderRadius: '8px',
            outline: 'none',
            transition: 'border-color 0.3s',
          }}
          onFocus={(e) => e.target.style.borderColor = '#e74c3c'}
          onBlur={(e) => e.target.style.borderColor = '#ddd'}
        />
        {searchQuery && (
          <p style={{ marginTop: '10px', fontSize: '14px', color: '#555' }}>
            Showing {filteredFraudCases.length} of {fraudCases.length} fraud cases
          </p>
        )}
      </div>

      {showForm && (
        <div style={{
          backgroundColor: '#ffebee',
          padding: '30px',
          borderRadius: '8px',
          marginBottom: '30px',
          border: '2px solid #e74c3c'
        }}>
          <h3 style={{ marginTop: 0, color: '#c62828' }}>
            {editingCase ? 'Edit Fraud Case' : 'Add New Fraud Case'}
          </h3>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label>Entity Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Violation Type *</label>
                <input
                  type="text"
                  value={formData.violation}
                  onChange={(e) => setFormData({...formData, violation: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                >
                  <option value="Pending Review">Pending Review</option>
                  <option value="Under Investigation">Under Investigation</option>
                  <option value="Legal Action Pending">Legal Action Pending</option>
                  <option value="Blacklisted">Blacklisted</option>
                  <option value="Resolved">Resolved</option>
                </select>
              </div>

              <div className="form-group">
                <label>Severity</label>
                <select
                  value={formData.severity}
                  onChange={(e) => setFormData({...formData, severity: e.target.value})}
                >
                  <option value="Critical">Critical</option>
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>

              <div className="form-group">
                <label>Warning Level</label>
                <select
                  value={formData.warningLevel}
                  onChange={(e) => setFormData({...formData, warningLevel: e.target.value})}
                >
                  <option value="Critical">Critical</option>
                  <option value="Warning">Warning</option>
                  <option value="Alert">Alert</option>
                </select>
              </div>

              <div className="form-group">
                <label>Total Complaints</label>
                <input
                  type="number"
                  value={formData.complaints}
                  onChange={(e) => setFormData({...formData, complaints: parseInt(e.target.value)})}
                  min="0"
                />
              </div>

              <div className="form-group">
                <label>Last Known Location</label>
                <input
                  type="text"
                  value={formData.lastKnownLocation}
                  onChange={(e) => setFormData({...formData, lastKnownLocation: e.target.value})}
                />
              </div>

              <div className="form-group">
                <label>Known Aliases</label>
                <input
                  type="text"
                  value={formData.aliases}
                  onChange={(e) => setFormData({...formData, aliases: e.target.value})}
                  placeholder="Separate by commas"
                />
              </div>

              <div className="form-group">
                <label>Victim Count</label>
                <input
                  type="text"
                  value={formData.victimCount}
                  onChange={(e) => setFormData({...formData, victimCount: e.target.value})}
                  placeholder="e.g., 15+"
                />
              </div>

              <div className="form-group">
                <label>Estimated Loss</label>
                <input
                  type="text"
                  value={formData.estimatedLoss}
                  onChange={(e) => setFormData({...formData, estimatedLoss: e.target.value})}
                  placeholder="e.g., ₹25,00,000"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows="3"
                required
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button type="submit" className="submit-btn" style={{ backgroundColor: '#e74c3c' }}>
                {editingCase ? 'Update Case' : 'Add Case'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="submit-btn"
                style={{ backgroundColor: '#95a5a6' }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#c62828', color: 'white' }}>
              <th style={{ padding: '15px', textAlign: 'left' }}>Case ID</th>
              <th style={{ padding: '15px', textAlign: 'left' }}>Entity Name</th>
              <th style={{ padding: '15px', textAlign: 'left' }}>Violation</th>
              <th style={{ padding: '15px', textAlign: 'left' }}>Severity</th>
              <th style={{ padding: '15px', textAlign: 'left' }}>Status</th>
              <th style={{ padding: '15px', textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredFraudCases.map((fraudCase, index) => (
              <tr key={fraudCase.caseId} style={{ backgroundColor: index % 2 === 0 ? '#f8f9fa' : 'white' }}>
                <td style={{ padding: '15px' }}>{fraudCase.caseId}</td>
                <td style={{ padding: '15px', fontWeight: 'bold' }}>{fraudCase.name}</td>
                <td style={{ padding: '15px' }}>{fraudCase.violation}</td>
                <td style={{ padding: '15px' }}>
                  <span style={{
                    backgroundColor: fraudCase.severity === 'High' || fraudCase.severity === 'Critical' ? '#ffcccc' : '#fff3cd',
                    color: fraudCase.severity === 'High' || fraudCase.severity === 'Critical' ? '#c62828' : '#856404',
                    padding: '5px 10px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: 'bold'
                  }}>
                    {fraudCase.severity}
                  </span>
                </td>
                <td style={{ padding: '15px' }}>{fraudCase.status}</td>
                <td style={{ padding: '15px', textAlign: 'center' }}>
                  {fraudCase.status === 'Pending Review' && (
                    <button
                      onClick={() => handleApprove(fraudCase)}
                      style={{
                        backgroundColor: '#27ae60',
                        color: 'white',
                        border: 'none',
                        padding: '8px 15px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        marginRight: '5px',
                        fontSize: '13px'
                      }}
                    >
                      Approve
                    </button>
                  )}
                  <button
                    onClick={() => handleEdit(fraudCase)}
                    style={{
                      backgroundColor: '#3498db',
                      color: 'white',
                      border: 'none',
                      padding: '8px 15px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      marginRight: '5px',
                      fontSize: '13px'
                    }}
                  >
                    Edit
                  </button>
                  {fraudCase.status !== 'Pending Review' && (
                    <button
                      onClick={() => handleToggleVisibility(fraudCase)}
                      style={{
                        backgroundColor: fraudCase.isHidden ? '#27ae60' : '#f39c12',
                        color: 'white',
                        border: 'none',
                        padding: '8px 15px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        marginRight: '5px',
                        fontSize: '13px'
                      }}
                    >
                      {fraudCase.isHidden ? 'Show' : 'Hide'}
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(fraudCase.caseId)}
                    style={{
                      backgroundColor: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      padding: '8px 15px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && (
        <div style={{
          marginTop: '20px',
          padding: '15px',
          backgroundColor: '#d1ecf1',
          borderRadius: '5px',
          color: '#0c5460',
          textAlign: 'center'
        }}>
          Loading fraud cases from Azure Table Storage...
        </div>
      )}
    </div>
  );
}

export default FraudCaseManager;
